import { useEffect, useRef, useState } from 'react'
import { api } from './api'

/**
 * Subscribes a staff console to a server-sent event feed.
 *
 * Every staff stream works the same way, so the mechanics live here once: ask
 * for a single-use ticket, open an `EventSource` with it, and reconnect by
 * hand with a backoff.
 *
 * A ticket is fetched first because `EventSource` cannot set an Authorization
 * header, and a staff session token in a URL is the last credential you want
 * in an access log. The ticket being single-use is also why reconnection is
 * done by hand: `EventSource`'s own retry would replay a spent URL forever.
 *
 * Extracted when the judges' tie-break panel needed the same thing as the
 * Admin dashboard. Two copies of reconnect-with-backoff is one too many, and
 * a bug fixed in one would have been missed in the other.
 */
export function useStaffStream<T>(opts: {
  /** POSTed to mint the ticket, e.g. `/api/admin/stream-ticket`. */
  ticketPath: string
  /** The SSE endpoint; the ticket is appended as `?t=`. */
  streamPath: string
  /** The `event:` name the server writes, e.g. `dashboard`. */
  eventName: string
  enabled: boolean
}) {
  const { ticketPath, streamPath, eventName, enabled } = opts

  const [data, setData] = useState<T | null>(null)
  const [state, setState] = useState<'connecting' | 'live' | 'error'>('connecting')

  /** Survives a reconnect, so a dropped stream never blanks the console. */
  const latest = useRef<T | null>(null)

  useEffect(() => {
    if (!enabled) return

    let source: EventSource | null = null
    let retry: number | null = null
    let cancelled = false
    let attempt = 0

    const backoff = () => {
      attempt += 1
      retry = window.setTimeout(connect, Math.min(1000 * 2 ** (attempt - 1), 15_000))
    }

    const connect = async () => {
      if (cancelled) return

      try {
        const { ticket } = await api<{ ticket: string }>(ticketPath, {
          method: 'POST',
          auth: 'staff',
        })
        if (cancelled) return

        source = new EventSource(`${streamPath}?t=${encodeURIComponent(ticket)}`)

        source.addEventListener(eventName, (event) => {
          const next = JSON.parse((event as MessageEvent).data) as T
          latest.current = next
          setData(next)
          setState('live')
          attempt = 0
        })

        source.onerror = () => {
          source?.close()
          source = null
          if (cancelled) return

          setState('error')
          backoff()
        }
      } catch {
        if (cancelled) return
        setState('error')
        backoff()
      }
    }

    void connect()

    return () => {
      cancelled = true
      if (retry) window.clearTimeout(retry)
      source?.close()
    }
  }, [ticketPath, streamPath, eventName, enabled])

  return { data: data ?? latest.current, state }
}
